import React, { useState, useEffect } from "react";
import axios from "axios";
import "./InfoPopup.css";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { analytics } from "../firebase/firebaseClient";
import { logEvent } from "firebase/analytics";
import { db } from "../firebase/firebaseClient";
import { doc, setDoc } from "firebase/firestore";

const savePopupActivity = async (type, data = {}) => {
  try {
    const session_id = localStorage.getItem("session_id") || "unknown_session";
    const email = localStorage.getItem("user_email") || "unknown_email";
    const timestamp = new Date().toISOString();

    await setDoc(
      doc(db, "user_popup_logs", `${type}_${Date.now()}`),
      {
        type,
        email,
        session_id,
        timestamp,
        ...data,
      }
    );

    console.log(`✔ Firestore saved: ${type}`);
  } catch (error) {
    console.error("❌ Firestore write failed:", error);
  }
};

const InfoPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [b2cData, setB2cData] = useState(null);
  const [weeklyProjectSummary, setWeeklyProjectSummary] = useState(null);
  const [monthlyProjectSummary, setMonthlyProjectSummary] = useState(null);
  const [achievementSummary, setAchievementSummary] = useState({ duration: "", data: [] });
  const [ykSummary, setYkSummary] = useState([]);

  const togglePopup = () => {
    const willOpen = !isOpen;
    setIsOpen(willOpen);
  
    if (willOpen) {
      // Firebase Analytics Event
      try {
        const session_id = localStorage.getItem("session_id");
        const email = localStorage.getItem("user_email");
        logEvent(analytics, "info_popup_opened", {
          opened_at: new Date().toISOString(),
          session_id,
          email,
        });
      } catch (e) {
        console.warn("Failed to log info_popup_opened", e);
      }
  
      // Firestore Event
      savePopupActivity("popup_open");
    }
  };
  

  const handleExportToExcel = () => {
    savePopupActivity("export_clicked");
  
    // Log export event
    if (typeof window !== "undefined" && analytics) {
      try {
        const session_id = localStorage.getItem("session_id") || null;
        const email = localStorage.getItem("user_email") || null;
        logEvent(analytics, "export_to_excel_clicked", {
          exported_at: new Date().toISOString(),
          session_id,
          email,
        });
      } catch (e) {
        console.warn("Failed to log export_to_excel_clicked", e);
      }
    }
  

    const workbook = XLSX.utils.book_new();

    const addSheet = (data, sheetName, headers = []) => {
      if (!data?.length) return;
      const sheetData = [headers, ...data];
      const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    };

    const formatSummary = (label, data) =>
      data?.map((row) => [
        label,
        row.duration,
        row.open,
        row.submit,
        row.br.total,
        row.br.schemes,
        row.br.dfl,
        row.br.prepaid_yc,
        row.br.loyalty_card,
        row.br.debit_yc,
      ]) || [];

    const summaryHeaders = [
      "Report",
      "Duration",
      "Open",
      "Submit",
      "BR Total",
      "BR Schemes",
      "DFL",
      "Prepaid YC",
      "Loyalty Card",
      "Debit YC",
    ];
    const summaryRows = [
      ...formatSummary("Weekly", summaryData?.weekly),
      ...formatSummary("Monthly", summaryData?.monthly),
      summaryData?.cumulative
        ? [
            "Cumulative",
            `${summaryData.monthly?.at(-1)?.duration} - till date`,
            summaryData.cumulative.open,
            summaryData.cumulative.submit,
            summaryData.cumulative.br.total,
            summaryData.cumulative.br.schemes,
            summaryData.cumulative.br.dfl,
            summaryData.cumulative.br.prepaid_yc,
            summaryData.cumulative.br.loyalty_card,
            summaryData.cumulative.br.debit_yc,
          ]
        : null,
    ].filter(Boolean);
    addSheet(summaryRows, "Summary", summaryHeaders);

    const b2cHeaders = ["Report", "Duration", "Schemes (BR)", "Loyalty YC", "Debit YC", "Prepaid YC"];
    const b2cRows = [
      ...(b2cData?.weekly || []).map((row) => [
        "Weekly",
        row.duration,
        row.br_schemes,
        row.br_loyalty_yc,
        row.br_debit_yc,
        row.br_prepaid_yc,
      ]),
      ...(b2cData?.monthly || []).map((row) => [
        "Monthly",
        row.duration,
        row.br_schemes,
        row.br_loyalty_yc,
        row.br_debit_yc,
        row.br_prepaid_yc,
      ]),
      b2cData?.cumulative
        ? [
            "Cumulative",
            b2cData.cumulative.duration,
            b2cData.cumulative.br_schemes,
            b2cData.cumulative.br_loyalty_yc,
            b2cData.cumulative.br_debit_yc,
            b2cData.cumulative.br_prepaid_yc,
          ]
        : null,
    ].filter(Boolean);
    addSheet(b2cRows, "B2C", b2cHeaders);

    if (weeklyProjectSummary?.data?.length) {
      const weeklyHeaders = ["Project Type", "Open", "Submitted", "BR"];
      const weeklyRows = weeklyProjectSummary.data.map((row) => [row.project_type, row.open_count, row.ds_count, row.br_count]);
      addSheet(weeklyRows, `Weekly Projects`, weeklyHeaders);
    }

    if (monthlyProjectSummary?.data?.length) {
      const monthlyHeaders = ["Project Type", "Open", "Submitted", "BR"];
      const monthlyRows = monthlyProjectSummary.data.map((row) => [row.project_type, row.Open, row.DS, row.BR]);
      addSheet(monthlyRows, `Monthly Projects`, monthlyHeaders);
    }

    if (achievementSummary?.data?.length) {
      const achievementHeaders = ["Category", "Submit (Month Start - Last Sunday)", "BR (Month Start - Last Sunday)"];
      const achievementRows = achievementSummary.data.map((row) => [row.Category, row.Submit_Month_Start_Last_Sunday, row.BR_Month_Start_Last_Sunday]);
      addSheet(achievementRows, `Achievements`, achievementHeaders);
    }

    if (ykSummary?.length) {
      const ykHeaders = [
        "Org Name",
        "State",
        "District",
        "Cumulative Open",
        `Monthly Open (${new Date().toLocaleString("en-US", { month: "short" })}’${new Date().getFullYear().toString().slice(-2)})`,
        `Weekly Open (${weeklyProjectSummary?.duration || ""})`,
      ];
      const ykRows = ykSummary.map((row) => {
        const loc = ykLocationMap[row.org_name] || {};
        return [row.org_name, loc.state, loc.district, row.open_cumulative, row.open_month, row.open_week];
      });
      const total = { open_cumulative: 0, open_month: 0, open_week: 0 };
      ykSummary.forEach((r) => {
        total.open_cumulative += r.open_cumulative || 0;
        total.open_month += r.open_month || 0;
        total.open_week += r.open_week || 0;
      });
      ykRows.push(["Total HESPL YKs", "", "", total.open_cumulative, total.open_month, total.open_week]);
      addSheet(ykRows, "YK Summary", ykHeaders);
    }

    const blob = XLSX.write(workbook, { bookType: "xlsx", type: "binary" });
    const buf = new ArrayBuffer(blob.length);
    const view = new Uint8Array(buf);
    for (let i = 0; i < blob.length; ++i) view[i] = blob.charCodeAt(i) & 0xff;
    const data = new Blob([buf], { type: "application/octet-stream" });
    saveAs(data, "Summary_Reports.xlsx");
  };

  useEffect(() => {
    if (isOpen && (!summaryData || !b2cData)) {
      axios
        .get("https://api-insights.haqdarshak.com/api/combined_br_summary")
        .then((res) => setSummaryData(res.data))
        .catch((err) => console.error("Failed to load summary:", err));

      axios
        .get("https://api-insights.haqdarshak.com/api/b2c_summary")
        .then((res) => setB2cData(res.data))
        .catch((err) => console.error("Failed to load B2C data:", err));

      axios
        .get("https://api-insights.haqdarshak.com/api/weekly_project_summary")
        .then((res) => setWeeklyProjectSummary(res.data))
        .catch((err) => console.error("Failed to load weekly project summary:", err));

      axios
        .get("https://api-insights.haqdarshak.com/api/monthly_project_summary")
        .then((res) => setMonthlyProjectSummary(res.data))
        .catch((err) => console.error("Failed to load monthly project summary:", err));

      axios
        .get("https://api-insights.haqdarshak.com/api/achievement_summary")
        .then((res) => setAchievementSummary(res.data))
        .catch((err) => console.error("Failed to load achievement summary:", err));

      axios
        .get("https://api-insights.haqdarshak.com/api/yojana_kendra_summary")
        .then((res) => setYkSummary(res.data))
        .catch((err) => console.error("Failed to load Yojana Kendra Summary:", err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const parseMonthYear = (str) => {
    const [monthStr, yearStr] = str.split(" ");
    const months = {
      Jan: 0,
      Feb: 1,
      Mar: 2,
      Apr: 3,
      May: 4,
      Jun: 5,
      Jul: 6,
      Aug: 7,
      Sep: 8,
      Oct: 9,
      Nov: 10,
      Dec: 11,
    };
    return new Date(+yearStr, months[monthStr.slice(0, 3)] || 0, 1);
  };

  const getEndDate = (duration) => {
    const parts = duration.split(" - ");
    const endPart = parts[1];
    const [day, month] = endPart.split(" ");
    const months = {
      Jan: 0,
      Feb: 1,
      Mar: 2,
      Apr: 3,
      May: 4,
      Jun: 5,
      Jul: 6,
      Aug: 7,
      Sep: 8,
      Oct: 9,
      Nov: 10,
      Dec: 11,
    };
    const year = new Date().getFullYear();
    return new Date(year, months[month], parseInt(day));
  };

  const renderRows = (label, data) => {
    if (!data) return null;

    let sortedData;
    if (label === "Weekly") {
      sortedData = [...data].sort((a, b) => getEndDate(b.duration) - getEndDate(a.duration));
    } else {
      sortedData = [...data].sort((a, b) => parseMonthYear(b.duration) - parseMonthYear(a.duration));
    }

    return sortedData.map((row, i) => (
      <tr key={`${label}-${i}`}>
        {i === 0 && (
          <td rowSpan={sortedData.length} style={{ fontWeight: "bold" }}>
            {label}
          </td>
        )}
        <td>{row.duration}</td>
        <td>{row.open}</td>
        <td>{row.submit}</td>
        <td>{row.br.total}</td>
        <td>{row.br.schemes}</td>
        <td>{row.br.dfl}</td>
        <td>{row.br.prepaid_yc}</td>
        <td>{row.br.loyalty_card}</td>
        <td>{row.br.debit_yc}</td>
      </tr>
    ));
  };

  const renderCumulativeRow = () => {
    if (!summaryData?.cumulative || !summaryData?.monthly?.length) return null;
    const sortedMonthly = [...summaryData.monthly].sort((a, b) => parseMonthYear(b.duration) - parseMonthYear(a.duration));
    const firstMonth = sortedMonthly[sortedMonthly.length - 1]?.duration || "";
    const durationText = `${firstMonth} - till date`;

    const row = summaryData.cumulative;
    return (
      <tr>
        <td style={{ fontWeight: "bold" }}>Cumulative</td>
        <td>{durationText}</td>
        <td>{row.open}</td>
        <td>{row.submit}</td>
        <td>{row.br.total}</td>
        <td>{row.br.schemes}</td>
        <td>{row.br.dfl}</td>
        <td>{row.br.prepaid_yc}</td>
        <td>{row.br.loyalty_card}</td>
        <td>{row.br.debit_yc}</td>
      </tr>
    );
  };

  const renderB2CRows = (label, data) => {
    if (!data) return null;
    const sorted = [...data].sort((a, b) => parseMonthYear(b.duration) - parseMonthYear(a.duration));
    return sorted.map((row, i) => (
      <tr key={`${label}-${i}`}>
        {i === 0 && (
          <td rowSpan={sorted.length} style={{ fontWeight: "bold" }}>
            {label}
          </td>
        )}
        <td>{row.duration}</td>
        <td>{row.br_schemes}</td>
        <td>{row.br_loyalty_yc}</td>
        <td>{row.br_debit_yc}</td>
        <td>{row.br_prepaid_yc}</td>
      </tr>
    ));
  };

  const renderB2CCumulativeRow = () => {
    if (!b2cData?.cumulative) return null;
    const row = b2cData.cumulative;
    return (
      <tr>
        <td style={{ fontWeight: "bold" }}>Cumulative</td>
        <td>{row.duration}</td>
        <td>{row.br_schemes}</td>
        <td>{row.br_loyalty_yc}</td>
        <td>{row.br_debit_yc}</td>
        <td>{row.br_prepaid_yc}</td>
      </tr>
    );
  };

  const renderWeeklyProjectSummary = () => {
    if (!weeklyProjectSummary) return null;

    return (
      <table className="styled-table">
        <thead>
          <tr>
            <th colSpan="4">Weekly Summary ({weeklyProjectSummary.duration})</th>
          </tr>
          <tr>
            <th>Project type</th>
            <th>Open</th>
            <th>Submitted</th>
            <th>Benefit received</th>
          </tr>
        </thead>
        <tbody>
          {weeklyProjectSummary.data.map((row, index) => (
            <tr key={index}>
              <td>{row.project_type}</td>
              <td>{row.open_count}</td>
              <td>{row.ds_count}</td>
              <td>{row.br_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderMonthlyProjectSummary = () => {
    if (!monthlyProjectSummary) return null;

    return (
      <table className="styled-table">
        <thead>
          <tr>
            <th colSpan="4">Monthly Summary ({monthlyProjectSummary.duration})</th>
          </tr>
          <tr>
            <th>Project type</th>
            <th>Open</th>
            <th>Submitted</th>
            <th>Benefit received</th>
          </tr>
        </thead>
        <tbody>
          {monthlyProjectSummary.data.map((row, index) => (
            <tr key={index}>
              <td>{row.project_type}</td>
              <td>{row.Open}</td>
              <td>{row.DS}</td>
              <td>{row.BR}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderAchievementSummary = () => {
    if (!achievementSummary.data?.length) return null;

    return (
      <table className="styled-table">
        <thead>
          <tr>
            <th colSpan="3">Achievement ({achievementSummary.duration})</th>
          </tr>
          <tr>
            <th>Achievement Type</th>
            <th>Submit</th>
            <th>BR</th>
          </tr>
        </thead>
        <tbody>
          {achievementSummary.data.map((row, index) => (
            <tr key={index}>
              <td>{row.Category}</td>
              <td>{row.Submit_Month_Start_Last_Sunday}</td>
              <td>{row.BR_Month_Start_Last_Sunday}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const ykLocationMap = {
    CG_Mahasamund_YK: { state: "CG", district: "Mahasamund" },
    "MP - MP_Chhindwara_YK": { state: "MP", district: "Chhindwara" },
    "MH_YK_Nashik": { state: "MH", district: "Nashik" },
    "MH_YK_Palghar": { state: "MH", district: "Palghar" },
    "RJ_YK_Jaipur": { state: "RJ", district: "Jaipur" },
  };

  const renderYojanaKendraSummary = () => {
    if (!ykSummary.length) return null;

    const totalRow = { open_cumulative: 0, open_month: 0, open_week: 0 };

    ykSummary.forEach((row) => {
      totalRow.open_cumulative += row.open_cumulative || 0;
      totalRow.open_month += row.open_month || 0;
      totalRow.open_week += row.open_week || 0;
    });
    const getCurrentMonthLabel = () => {
      const now = new Date();
      const shortMonth = now.toLocaleString("en-US", { month: "short" });
      const shortYear = now.getFullYear().toString().slice(-2);
      return `${shortMonth}’${shortYear}`;
    };

    return (
      <table className="styled-table">
        <thead>
          <tr>
            <th colSpan="6" style={{ textAlign: "center" }}>
              Yojana Kendra Summary
            </th>
          </tr>
          <tr>
            <th colSpan="3">Location</th>
            <th colSpan="3" style={{ textAlign: "center" }}>
              Applications Data
            </th>
          </tr>
          <tr>
            <th>Org name</th>
            <th>State</th>
            <th>District</th>
            <th>Cumulative (Open applications)</th>
            <th>Monthly Open ({getCurrentMonthLabel()})</th>
            <th>Weekly Open ({weeklyProjectSummary?.duration || "Loading..."})</th>
          </tr>
        </thead>
        <tbody>
          {ykSummary.map((row, index) => {
            const location = ykLocationMap[row.org_name] || { state: "", district: "" };
            return (
              <tr key={index}>
                <td>{row.org_name}</td>
                <td>{location.state}</td>
                <td>{location.district}</td>
                <td>{row.open_cumulative}</td>
                <td>{row.open_month}</td>
                <td>{row.open_week}</td>
              </tr>
            );
          })}
          <tr>
            <td colSpan="3" style={{ fontWeight: "bold" }}>
              Total HESPL YKs
            </td>
            <td>{totalRow.open_cumulative}</td>
            <td>{totalRow.open_month}</td>
            <td>{totalRow.open_week}</td>
          </tr>
        </tbody>
      </table>
    );
  };

  return (
    <div className="popup-container">
      <div className="summary-card-wrapper">
        <div className="summary-glass-card" onClick={togglePopup}>
          <div className="glow-ring"></div>
          <div className="animated-graph-bg">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>

          <div className="summary-icon"></div>

          <div className="summary-details">
            <h2>📊 Weekly Scorecard</h2>
            <p>Unlock insights from recent achievements and trends</p>
          </div>

          <div className="summary-action">
            <span>➤</span>
          </div>

          <div className="shimmer-line"></div>
        </div>
      </div>

      {isOpen && (
        <div className="popup-overlay" onClick={togglePopup}>
          <div className="popup-box" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={togglePopup}>
              ×
            </button>
            <div className="popup-header">
              <h2 className="popup-title">Summary Reports</h2>
              <a href="/Manual for Interpreting the Weekly Scorecard.pdf" download className="download-btn" title="Download PDF">
                🔗 <span className="btn-label">Manual for Interpreting the Weekly Scorecard</span>
              </a>
            </div>

            <div className="popup-content">
              <table className="styled-table">
                <thead>
                  <tr>
                    <th colSpan="10">Summary</th>
                  </tr>
                  <tr>
                    <th rowSpan="2">Report</th>
                    <th rowSpan="2">Duration</th>
                    <th rowSpan="2">Open</th>
                    <th rowSpan="2">Submit</th>
                    <th colSpan="6">BR</th>
                  </tr>
                  <tr>
                    <th>Total</th>
                    <th>Schemes</th>
                    <th>DFL</th>
                    <th>Prepaid YC</th>
                    <th>Loyalty Card</th>
                    <th>Debit YC</th>
                  </tr>
                </thead>
                <tbody>
                  {renderRows("Weekly", summaryData?.weekly)}
                  {renderRows("Monthly", summaryData?.monthly)}
                  {renderCumulativeRow()}
                </tbody>
              </table>

              <table className="styled-table">
                <thead>
                  <tr>
                    <th colSpan="6">Overall B2C</th>
                  </tr>
                  <tr>
                    <th>Report</th>
                    <th>Duration</th>
                    <th>Schemes (BR)</th>
                    <th>Loyalty Yojana Card</th>
                    <th>Debit Yojana Card</th>
                    <th>Prepaid Yojana Card</th>
                  </tr>
                </thead>
                <tbody>
                  {renderB2CRows("Weekly", b2cData?.weekly)}
                  {renderB2CRows("Monthly", b2cData?.monthly)}
                  {renderB2CCumulativeRow()}
                </tbody>
              </table>

              {renderWeeklyProjectSummary()}
              {renderMonthlyProjectSummary()}
              {renderAchievementSummary()}

              {renderYojanaKendraSummary()}
              <div style={{ textAlign: "right", marginTop: "1rem" }}>
                <button className="export-btn" onClick={handleExportToExcel}>
                  ⬇️ Export to Excel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InfoPopup;
